import * as React from 'react'

import { SquarePlusIcon } from 'lucide-react'
import { action, computed, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'

import { SAVE_PROJECT_COLORING_OPTIONS } from '../../stores/EventStore/eventConstants'
import EventsStore from '../../stores/EventStore/Events.store'
import InitialState from '../../stores/InitialState'
import { areArraysEqual } from '../../utils/util'
import ColorPalette from './ColorPalette'

import './ColorPicker.scss'

interface IProps {
  colors?: string[]
  value: string
  arePickerExpanded: boolean
  canAddNewColors?: boolean
  state?: InitialState
  eventsStore?: EventsStore

  onChange(color: string): void
}

@inject('state', 'eventsStore')
@observer
export default class ColorPicker extends React.Component<IProps> {
  @observable private areColorPaletteExpanded = false
  @observable private currentColor = null
  @observable private localColors: string[] = []
  @observable private value: string = null

  public constructor(props: IProps) {
    super(props)
    this.value = props.value
    this.localColors = props.colors
    makeObservable(this)
  }

  public componentDidUpdate(props: IProps) {
    if (this.props.value !== props.value) {
      this.currentColor = props.value
    }
    if (this.value !== this.props.value) this.value = this.props.value
    if (
      this.localColors &&
      this.props.colors &&
      !areArraysEqual(this.localColors, this.props.colors)
    )
      this.localColors = this.props.colors
  }

  @computed
  private get colors() {
    const fullColors = [...this.localColors]
    if (this.value && !this.localColors.includes(this.value)) {
      fullColors.push(this.value)
    }
    if (this.currentColor && !this.localColors.includes(this.currentColor)) {
      fullColors.push(this.currentColor)
    }

    return fullColors
  }

  public render() {
    const {
      value,
      arePickerExpanded,
      canAddNewColors = true,
      onChange,
    } = this.props
    return (
      <div className="colors-container">
        {this.colors.map((color, idx) => (
          <div
            key={idx}
            className={classList({
              'color-option': true,
              selected: color === value,
            })}
            style={{ backgroundColor: color }}
            onClick={onChange.bind(this, color)}
          />
        ))}
        {canAddNewColors && (
          <Popover
            open={arePickerExpanded && this.areColorPaletteExpanded}
            onOpenChange={this.onPaletteOpenChange}
          >
            <PopoverTrigger
              nativeButton={false}
              render={<div className="color-option pointer" />}
            >
              <SquarePlusIcon
                size={20}
                strokeWidth={1.5}
                className="tw:text-muted-foreground"
              />
            </PopoverTrigger>
            <PopoverContent className="tw:w-auto tw:bg-transparent tw:p-0 tw:shadow-none tw:ring-0">
              {this.renderColorPaletteSelector()}
            </PopoverContent>
          </Popover>
        )}
      </div>
    )
  }

  private renderColorPaletteSelector = () => {
    return (
      this.areColorPaletteExpanded && (
        <ColorPalette
          value={this.currentColor || this.props.value}
          onChange={this.onChangeComplete}
          onClose={this.collapseColorPalette}
        />
      )
    )
  }

  @action.bound
  private collapseColorPalette() {
    this.setNewColor()
    this.areColorPaletteExpanded = false
  }

  @action.bound
  private onPaletteOpenChange(isOpen: boolean) {
    this.setNewColor()
    this.areColorPaletteExpanded = isOpen
  }

  @action.bound
  private onChangeComplete(color: string) {
    if (!this.colors.includes(color)) {
      this.currentColor = color
    } else {
      this.currentColor = null
    }
  }

  @action.bound
  private addColorToOptions(color: string) {
    const options = this.props.state.projectColoringOptions
    const colors = options.colors || []
    if (!colors.includes(color)) {
      colors.push(color)
      options.colors = colors
      this.props.eventsStore.dispatch(SAVE_PROJECT_COLORING_OPTIONS, options)
    }
  }

  @action.bound
  private setNewColor() {
    if (this.currentColor) {
      this.addColorToOptions(this.currentColor)
      this.currentColor = null
    }
  }
}
